package com.eladfinish.bible.contest.screens.search.ui

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyListScope
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.text.TextAutoSize
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.LocalTextStyle
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.SegmentedButton
import androidx.compose.material3.SegmentedButtonDefaults
import androidx.compose.material3.SingleChoiceSegmentedButtonRow
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.layout
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextDirection
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.isSpecified
import androidx.compose.ui.unit.sp
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.screens.search.mvi.AutocompleteUiState
import com.eladfinish.bible.contest.screens.search.suggestions.AutocompleteSuggestion
import com.eladfinish.bible.contest.screens.search.suggestions.SuggestionsPage
import com.eladfinish.bible.contest.search_popular_searches_all_time
import com.eladfinish.bible.contest.search_suggestions_fill_field
import com.eladfinish.bible.contest.search_suggestions_more
import com.eladfinish.bible.contest.search_suggestions_page_results
import com.eladfinish.bible.contest.search_suggestions_personal
import com.eladfinish.bible.contest.search_suggestions_popular
import com.eladfinish.bible.contest.search_suggestions_this_month
import com.eladfinish.bible.contest.search_suggestions_this_week
import com.eladfinish.bible.contest.ui.common.PreviewTheme
import com.eladfinish.bible.contest.ui.extensions.navigationBarContentPadding
import com.eladfinish.bible.contest.ui.extensions.navigationBarInsetPadding
import com.eladfinish.bible.contest.ui.icons.AppIcons
import dev.icerock.moko.resources.StringResource
import dev.icerock.moko.resources.compose.stringResource
import kotlinx.collections.immutable.persistentListOf

const val AUTOCOMPLETE_ROW_TEST_TAG = "autocomplete_row"
const val SUGGESTIONS_CHIP_TEST_TAG = "suggestions_chip"

/** The autocomplete shown on the results page while the user types. */
@Composable
fun AutocompleteSuggestionsContent(
    state: AutocompleteUiState.ForPrefix,
    onSuggestionClick: (AutocompleteSuggestion) -> Unit,
    onFillClick: (AutocompleteSuggestion) -> Unit,
    onMoreClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    LazyColumn(
        modifier = modifier.fillMaxSize(),
        contentPadding = navigationBarContentPadding(),
    ) {
        suggestionRows(
            suggestions = state.suggestions,
            prefix = state.prefix,
            onSuggestionClick = onSuggestionClick,
            onFillClick = onFillClick,
        )
        if (state.suggestions.isNotEmpty()) {
            item(key = "more") {
                MoreRow(onClick = onMoreClick)
            }
        }
    }
}

/** One of the two popular pages: the rows, then the row that opens the full list. */
@Composable
fun PopularSearchesPage(
    rows: List<AutocompleteSuggestion>,
    onSuggestionClick: (AutocompleteSuggestion) -> Unit,
    onFillClick: (AutocompleteSuggestion) -> Unit,
    onMoreClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    LazyColumn(
        modifier = modifier.fillMaxSize(),
        contentPadding = navigationBarContentPadding(),
    ) {
        suggestionRows(
            suggestions = rows,
            prefix = "",
            onSuggestionClick = onSuggestionClick,
            onFillClick = onFillClick,
        )
        if (rows.isNotEmpty()) {
            item(key = "more") {
                MoreRow(onClick = onMoreClick)
            }
        }
    }
}

/**
 * The three choices under the pages, as one segmented row centred at the bottom. The recent
 * choice's label follows the snapshot's window ("this week" / "this month").
 *
 * The row takes the whole width (up to [SUGGESTIONS_CHIPS_MAX_WIDTH]) so each button gets a third
 * of it instead of hugging its label, and a label that is still too long for its third (French,
 * Russian) shrinks its font rather than touching the borders or losing its end.
 */
@Composable
fun SuggestionsPageChips(
    selected: SuggestionsPage,
    recentDays: Int,
    onSelected: (SuggestionsPage) -> Unit,
    modifier: Modifier = Modifier,
) {
    Row(
        modifier = modifier
            .fillMaxWidth()
            .navigationBarInsetPadding()
            .padding(horizontal = 8.dp, vertical = 4.dp),
        horizontalArrangement = Arrangement.Center,
    ) {
        SingleChoiceSegmentedButtonRow(
            modifier = Modifier
                .widthIn(max = SUGGESTIONS_CHIPS_MAX_WIDTH)
                .fillMaxWidth(),
        ) {
            SuggestionsPage.entries.forEachIndexed { index, page ->
                SegmentedButton(
                    selected = page == selected,
                    onClick = { onSelected(page) },
                    shape = SegmentedButtonDefaults.itemShape(index = index, count = SuggestionsPage.entries.size),
                    modifier = Modifier.testTag("$SUGGESTIONS_CHIP_TEST_TAG:${page.name}"),
                ) {
                    SegmentedButtonLabel(text = stringResource(page.label(recentDays)))
                }
            }
        }
    }
}

/**
 * A one-line label that fits its segmented button whatever the language.
 *
 * Material3's [SegmentedButton] lays the label out next to a slot for the check icon
 * ([SegmentedButtonDefaults.IconSize] plus its spacing), and keeps that slot even while no icon
 * shows. It measures the label with the full padded width though, so a label that fills it
 * overflows the button by the slot's width: the text touches the borders, and when the check
 * appears it pushes the text out. The label is therefore measured with the slot subtracted, and
 * shrinks its font down to [SEGMENTED_LABEL_MIN_FONT_SIZE] when even that is too narrow.
 */
@Composable
private fun SegmentedButtonLabel(
    text: String,
) {
    val fontSize = LocalTextStyle.current.fontSize
    Text(
        text = text,
        modifier = Modifier.leaveRoomForSegmentedButtonIcon(),
        maxLines = 1,
        softWrap = false,
        overflow = TextOverflow.Ellipsis,
        autoSize = TextAutoSize.StepBased(
            minFontSize = SEGMENTED_LABEL_MIN_FONT_SIZE,
            maxFontSize = if (fontSize.isSpecified) fontSize else SEGMENTED_LABEL_MAX_FONT_SIZE,
            stepSize = 0.5.sp,
        ),
    )
}

private fun Modifier.leaveRoomForSegmentedButtonIcon(): Modifier =
    layout { measurable, constraints ->
        val iconSlot = (SegmentedButtonDefaults.IconSize + SEGMENTED_ICON_SPACING).roundToPx()
        val maxWidth =
            if (constraints.hasBoundedWidth) (constraints.maxWidth - iconSlot).coerceAtLeast(0) else constraints.maxWidth
        val placeable = measurable.measure(constraints.copy(minWidth = 0, maxWidth = maxWidth))
        layout(placeable.width, placeable.height) {
            placeable.placeRelative(0, 0)
        }
    }

/** Wider than this and the three buttons look like a toolbar (tablets, desktop, web). */
private val SUGGESTIONS_CHIPS_MAX_WIDTH = 480.dp

/** Material3's spacing between the segmented button's icon and label; private there. */
private val SEGMENTED_ICON_SPACING = 8.dp

private val SEGMENTED_LABEL_MIN_FONT_SIZE = 10.sp

/** Material3's label size, for a text style with no size of its own. */
private val SEGMENTED_LABEL_MAX_FONT_SIZE = 14.sp

fun SuggestionsPage.label(recentDays: Int): StringResource =
    when (this) {
        SuggestionsPage.RESULTS -> SharedRes.strings.search_suggestions_page_results
        SuggestionsPage.HOT_NOW ->
            if (recentDays <= 7) SharedRes.strings.search_suggestions_this_week else SharedRes.strings.search_suggestions_this_month
        SuggestionsPage.MOST_POPULAR -> SharedRes.strings.search_popular_searches_all_time
    }

private fun LazyListScope.suggestionRows(
    suggestions: List<AutocompleteSuggestion>,
    prefix: String,
    onSuggestionClick: (AutocompleteSuggestion) -> Unit,
    onFillClick: (AutocompleteSuggestion) -> Unit,
) {
    items(
        items = suggestions,
        key = { "${it.origin}:${it.query}" },
    ) { suggestion ->
        SuggestionRow(
            suggestion = suggestion,
            prefix = prefix,
            onClick = { onSuggestionClick(suggestion) },
            onFillClick = { onFillClick(suggestion) },
        )
        HorizontalDivider()
    }
}

@Composable
private fun MoreRow(
    onClick: () -> Unit,
) {
    Text(
        text = stringResource(SharedRes.strings.search_suggestions_more),
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .padding(16.dp),
        style = MaterialTheme.typography.labelLarge,
        color = MaterialTheme.colorScheme.primary,
        textAlign = TextAlign.Center,
    )
}

@Composable
private fun SuggestionRow(
    suggestion: AutocompleteSuggestion,
    prefix: String,
    onClick: () -> Unit,
    onFillClick: () -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .testTag(AUTOCOMPLETE_ROW_TEST_TAG)
            .padding(start = 16.dp, top = 2.dp, bottom = 2.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        val isPersonal = suggestion.origin == AutocompleteSuggestion.Origin.PERSONAL

        Icon(
            imageVector = if (isPersonal) AppIcons.History else AppIcons.TrendingUp,
            contentDescription = stringResource(
                if (isPersonal) SharedRes.strings.search_suggestions_personal else SharedRes.strings.search_suggestions_popular,
            ),
            modifier = Modifier
                .padding(end = 12.dp)
                .size(20.dp),
            tint = MaterialTheme.colorScheme.onSurfaceVariant,
        )

        Text(
            text = suggestion.query.highlightingPrefix(prefix),
            modifier = Modifier.weight(1f),
            style = MaterialTheme.typography.titleMedium.copy(
                textDirection = TextDirection.ContentOrRtl,
            ),
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
        )

        // Isolated as left-to-right so the digits read the same inside a right-to-left row.
        Text(
            text = "⁦${suggestion.count}⁩",
            modifier = Modifier.padding(horizontal = 8.dp),
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )

        IconButton(onClick = onFillClick) {
            Icon(
                imageVector = AppIcons.NorthWest,
                contentDescription = stringResource(SharedRes.strings.search_suggestions_fill_field),
                tint = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
    }
}

/** The typed prefix in bold, the completion in the regular weight, so the eye lands on what is new. */
@Composable
private fun String.highlightingPrefix(prefix: String) =
    buildAnnotatedString {
        if (prefix.isNotEmpty() && startsWith(prefix)) {
            withStyle(SpanStyle(fontWeight = FontWeight.Bold, color = MaterialTheme.colorScheme.primary)) {
                append(prefix)
            }
            append(substring(prefix.length))
        } else {
            append(this@highlightingPrefix)
        }
    }

@Preview(showBackground = true)
@Composable
private fun AutocompleteForPrefixPreview() {
    PreviewTheme {
        AutocompleteSuggestionsContent(
            state = AutocompleteUiState.ForPrefix(
                prefix = "ס",
                suggestions = persistentListOf(
                    AutocompleteSuggestion("סוכות", 3, AutocompleteSuggestion.Origin.PERSONAL),
                    AutocompleteSuggestion("סכות", 25, AutocompleteSuggestion.Origin.POPULAR),
                    AutocompleteSuggestion("סכה", 19, AutocompleteSuggestion.Origin.POPULAR),
                ),
            ),
            onSuggestionClick = {},
            onFillClick = {},
            onMoreClick = {},
        )
    }
}

@Preview(showBackground = true)
@Composable
private fun SuggestionsPageChipsPreview() {
    PreviewTheme {
        SuggestionsPageChips(selected = SuggestionsPage.HOT_NOW, recentDays = 7, onSelected = {})
    }
}
